/* ==========================================================================
   KAN — funnel section behaviour.

   Same contract as kan-sections.js: push [name, init] onto window.KanInit and
   let kan-motion.js mount it. Sections that only need scroll position use the
   `update` hook; the rest bind their own listeners and clean up in `destroy`,
   so a Theme Editor re-render never leaves a duplicate listener behind.
   ========================================================================== */

/* --------------------------------------------------------------------------
   VIDEO — play only while on screen; click-to-load for external embeds.
   -------------------------------------------------------------------------- */
(window.KanInit = window.KanInit || []).push([
  'kan-video',
  function (root, KM) {
    var video = root.querySelector('[data-video]');
    var facade = root.querySelector('[data-facade]');
    var autoplay = root.getAttribute('data-autoplay') !== 'false';
    var observer = null;

    if (facade) {
      facade.addEventListener('click', function () {
        var url = facade.getAttribute('data-embed');
        if (!url) return;

        /* Normalise a watch/share URL into an embeddable one. Anything we do
           not recognise is passed through untouched rather than guessed at. */
        var src = url;
        var yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/);
        var vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
        if (yt) {
          src = 'https://www.youtube-nocookie.com/embed/' + yt[1] + '?autoplay=1&rel=0';
        } else if (vimeo) {
          src = 'https://player.vimeo.com/video/' + vimeo[1] + '?autoplay=1';
        }

        var frame = document.createElement('iframe');
        frame.src = src;
        frame.title = facade.getAttribute('aria-label') || 'Video';
        frame.allow = 'accelerometer; autoplay; encrypted-media; picture-in-picture; fullscreen';
        frame.allowFullscreen = true;
        frame.loading = 'lazy';
        frame.className = 'kan-video__embed';
        facade.replaceWith(frame);
        frame.focus();
      });
    }

    if (video) {
      video.muted = true;
      video.playsInline = true;

      if (autoplay && !KM.reduced() && 'IntersectionObserver' in window) {
        observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                /* play() rejects on some browsers (low-power mode, no user
                   gesture). The poster frame stays visible, so swallow it. */
                var p = video.play();
                if (p && p.catch) p.catch(function () {});
              } else {
                video.pause();
              }
            });
          },
          { threshold: 0.25 }
        );
        observer.observe(video);
      }
    }

    return {
      destroy: function () {
        if (observer) observer.disconnect();
        if (video) video.pause();
      }
    };
  }
]);

/* --------------------------------------------------------------------------
   BUNDLE SELECTOR — reveal the chosen option's own server-rendered form.
   No price maths here on purpose; see the section comment.
   -------------------------------------------------------------------------- */
(window.KanInit = window.KanInit || []).push([
  'kan-bundle',
  function (root, KM) {
    var radios = KM.toArray(root.querySelectorAll('[data-radio]'));
    var forms = KM.toArray(root.querySelectorAll('[data-form]'));
    var labels = KM.toArray(root.querySelectorAll('[data-opt]'));
    if (!radios.length || !forms.length) return null;

    function select(index) {
      forms.forEach(function (form) {
        form.hidden = form.getAttribute('data-form') !== String(index);
      });
      labels.forEach(function (label) {
        label.classList.toggle('is-selected', label.getAttribute('data-opt') === String(index));
      });
    }

    function onChange(event) {
      select(event.target.value);
    }

    radios.forEach(function (radio) {
      radio.addEventListener('change', onChange);
    });

    var checked = root.querySelector('[data-radio]:checked');
    select(checked ? checked.value : 0);

    return {
      destroy: function () {
        radios.forEach(function (radio) {
          radio.removeEventListener('change', onChange);
        });
      }
    };
  }
]);

/* --------------------------------------------------------------------------
   COST PER USE — count whole numbers up once, when scrolled into view.
   Money is never animated (Liquid renders it); only integers count.
   -------------------------------------------------------------------------- */
(window.KanInit = window.KanInit || []).push([
  'kan-value',
  function (root, KM) {
    var nums = KM.toArray(root.querySelectorAll('[data-count]'));
    if (!nums.length) return null;

    var done = false;

    return {
      update: function () {
        if (done) return;
        if (KM.reduced()) {
          done = true;
          return;
        }

        var rect = root.getBoundingClientRect();
        if (rect.top > window.innerHeight * 0.85 || rect.bottom < 0) return;

        done = true;
        nums.forEach(function (el) {
          var target = parseInt(el.getAttribute('data-count'), 10);
          if (isNaN(target)) return;

          var started = null;
          var duration = 900;

          function step(now) {
            if (started === null) started = now;
            var t = KM.clamp((now - started) / duration);
            el.textContent = String(Math.round(KM.easeOut(t) * target));
            if (t < 1) requestAnimationFrame(step);
          }

          el.textContent = '0';
          requestAnimationFrame(step);
        });
      }
    };
  }
]);

/* --------------------------------------------------------------------------
   DRAG COMPARISON — range input drives a CSS custom property.
   -------------------------------------------------------------------------- */
(window.KanInit = window.KanInit || []).push([
  'kan-ba-slider',
  function (root) {
    var range = root.querySelector('[data-range]');
    var stage = root.querySelector('[data-stage]');
    if (!range || !stage) return null;

    function paint() {
      stage.style.setProperty('--pos', range.value + '%');
    }

    range.addEventListener('input', paint);
    paint();

    return {
      destroy: function () {
        range.removeEventListener('input', paint);
      }
    };
  }
]);

/* --------------------------------------------------------------------------
   EMAIL CAPTURE — scroll depth AND dwell time, once per session, never over
   an active cart. Deliberately not exit-intent (does not exist on touch).
   -------------------------------------------------------------------------- */
(window.KanInit = window.KanInit || []).push([
  'kan-capture',
  function (root, KM) {
    var KEY = 'kan:capture';
    var panel = root.querySelector('.kan-capture__panel');
    if (!panel) return null;

    var depth = parseFloat(root.getAttribute('data-depth')) || 40;
    var delay = (parseFloat(root.getAttribute('data-delay')) || 0) * 1000;
    var cartCount = parseInt(root.getAttribute('data-cart-count'), 10) || 0;

    /* Never interrupt someone who is already buying. */
    var suppressed = cartCount > 0;
    try {
      if (sessionStorage.getItem(KEY) === '1') suppressed = true;
    } catch (e) {
      /* storage unavailable — fall through and allow one show */
    }

    /* A successful signup re-renders the section with the form's success
       state, so open it straight away rather than making them scroll again. */
    var posted = !!root.querySelector('.kan-capture__ok');

    var opened = false;
    /* Separate from `opened`: once the visitor closes it, it must stay closed.
       Without this, the next scroll frame sees `opened === false`, the depth
       condition still true, and immediately re-opens it. */
    var dismissed = false;
    var eligibleAt = Date.now() + delay;
    var lastFocus = null;

    function remember() {
      try {
        sessionStorage.setItem(KEY, '1');
      } catch (e) {
        /* ignore */
      }
    }

    function open() {
      if (opened) return;
      opened = true;
      lastFocus = document.activeElement;
      root.hidden = false;
      remember();

      var first = root.querySelector('[data-first]') || root.querySelector('[data-close]');
      if (first && first.focus) first.focus();
      document.addEventListener('keydown', onKey);
    }

    /* Deliberately not guarded on `opened`. Closing must always hide, even if
       something else made the panel visible without going through open() —
       otherwise the close button silently does nothing and the visitor is
       trapped behind the overlay. */
    function close() {
      opened = false;
      dismissed = true;
      root.hidden = true;
      remember();
      document.removeEventListener('keydown', onKey);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    function onKey(event) {
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (event.key !== 'Tab') return;

      /* Keep focus inside the dialog while it is open. */
      var focusable = KM.toArray(
        panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function onClose(event) {
      event.preventDefault();
      close();
    }

    var closers = KM.toArray(root.querySelectorAll('[data-close]'));
    closers.forEach(function (el) {
      el.addEventListener('click', onClose);
    });

    if (posted) open();

    return {
      update: function () {
        if (opened || dismissed || suppressed || posted) return;
        if (Date.now() < eligibleAt) return;

        var scrolled = (KM.scrollY() / KM.scrollMax()) * 100;
        if (scrolled >= depth) open();
      },

      destroy: function () {
        document.removeEventListener('keydown', onKey);
        closers.forEach(function (el) {
          el.removeEventListener('click', onClose);
        });
      }
    };
  }
]);

/* --------------------------------------------------------------------------
   CART LADDER — upgrade rung.

   The button is a plain link to the upgrade product, so it works with no JS.
   Here we enhance it into an actual swap: add the upgrade, drop the entry
   product, then land on /cart so the customer reviews Shopify's own totals
   rather than anything this file calculated.

   Not registered through KanMotion: it lives in the cart drawer, which Horizon
   re-renders on every cart change, so the listener is delegated from document
   and survives those re-renders.
   -------------------------------------------------------------------------- */
(function () {
  'use strict';

  var BUSY = 'is-busy';

  function cartUrl(path) {
    var root = (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';
    return root + path;
  }

  document.addEventListener('click', function (event) {
    var btn = event.target.closest ? event.target.closest('[data-upgrade-to]') : null;
    if (!btn) return;

    var to = btn.getAttribute('data-upgrade-to');
    var from = btn.getAttribute('data-upgrade-from');
    if (!to) return; /* let the link navigate */

    event.preventDefault();
    if (btn.classList.contains(BUSY)) return;
    btn.classList.add(BUSY);

    var json = { 'Content-Type': 'application/json' };

    /* Add first, remove second. If the remove fails the customer ends up with
       both items rather than none — recoverable, and /cart shows the truth. */
    fetch(cartUrl('/add.js'), {
      method: 'POST',
      headers: json,
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: Number(to), quantity: 1 }] })
    })
      .then(function () {
        if (!from) return null;
        var updates = {};
        updates[from] = 0;
        return fetch(cartUrl('/update.js'), {
          method: 'POST',
          headers: json,
          credentials: 'same-origin',
          body: JSON.stringify({ updates: updates })
        });
      })
      .catch(function () {
        /* swallow — the redirect below shows the real cart either way */
      })
      .then(function () {
        window.location.href = cartUrl('');
      });
  });
})();
